import React, { useCallback, useEffect, useState } from 'react';
import axios from '../config/axios';

const panelStyle = {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    borderRadius: '20px',
    padding: '24px',
    color: 'white',
    marginBottom: '32px',
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.22)',
};

export default function FriendsPanel({ kidUserId }) {
    const [friends, setFriends] = useState([]);
    const [feed, setFeed] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('feed');
    const [username, setUsername] = useState('');
    const [message, setMessage] = useState(null);

    const fetchData = useCallback(async (silent = false) => {
        if (!silent) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }

        try {
            const [friendsRes, feedRes] = await Promise.all([
                axios.get('/api/v1/friends'),
                axios.get('/api/v1/friends/feed'),
            ]);
            setFriends(Array.isArray(friendsRes.data) ? friendsRes.data : []);
            setFeed(Array.isArray(feedRes.data) ? feedRes.data : []);
        } catch (error) {
            console.error('Failed to fetch friends data:', error);
            setMessage({ type: 'error', text: 'Failed to load friends right now.' });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        if (!kidUserId) {
            return;
        }
        fetchData();
    }, [fetchData, kidUserId]);

    const handleSendRequest = async (event) => {
        event.preventDefault();
        if (!username.trim()) {
            setMessage({ type: 'error', text: 'Enter a username first.' });
            return;
        }

        try {
            const response = await axios.post('/api/v1/friends/request', {
                username: username.trim(),
            });
            setMessage({
                type: 'success',
                text: response.data?.message || 'Friend request sent.',
            });
            setUsername('');
        } catch (error) {
            console.error('Failed to send friend request:', error);
            setMessage({
                type: 'error',
                text: error.response?.data || 'Failed to send friend request.',
            });
        }
    };

    return (
        <section style={panelStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.5rem' }}>👥 Friends</h2>
                    <p style={{ margin: '8px 0 0', color: 'rgba(255,255,255,0.75)' }}>
                        See what your friends are doing and send new requests.
                    </p>
                </div>
                <button
                    onClick={() => fetchData(true)}
                    aria-label={refreshing ? 'Refreshing friends' : 'Refresh friends list'}
                    style={{
                        background: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        color: 'white',
                        borderRadius: '999px',
                        padding: '10px 16px',
                        cursor: 'pointer',
                        fontWeight: 600,
                    }}
                >
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            <form onSubmit={handleSendRequest} style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder="Search username..."
                    aria-label="Friend username to search"
                    style={{
                        flex: 1,
                        minWidth: '220px',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.08)',
                        color: 'white',
                    }}
                />
                <button
                    type="submit"
                    aria-label="Send friend request"
                    style={{
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        fontWeight: 700,
                    }}
                >
                    Send Request
                </button>
            </form>

            {message && (
                <div
                    role="alert"
                    style={{
                        marginBottom: '16px',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        background: message.type === 'success' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                        border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
                    }}
                >
                    {message.text}
                </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                {[
                    ['feed', `📰 Activity (${feed.length})`],
                    ['friends', `🤝 Friends (${friends.length})`],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        role="tab"
                        aria-selected={activeTab === key}
                        aria-label={label}
                        style={{
                            background: activeTab === key ? 'white' : 'rgba(255,255,255,0.08)',
                            color: activeTab === key ? '#0f172a' : 'white',
                            border: 'none',
                            borderRadius: '999px',
                            padding: '10px 16px',
                            cursor: 'pointer',
                            fontWeight: 700,
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div style={{ color: 'rgba(255,255,255,0.75)' }}>Loading friends...</div>
            ) : activeTab === 'friends' ? (
                friends.length === 0 ? (
                    <div style={{ padding: '24px', borderRadius: '16px', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.72)' }}>
                        No friends yet. Send the first request above.
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '12px' }}>
                        {friends.map((friend) => (
                            <div key={friend.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderRadius: '16px', background: 'rgba(255,255,255,0.08)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ fontSize: '2rem' }}>{friend.avatar || '👤'}</div>
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{friend.display_name || friend.username}</div>
                                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>@{friend.username}</div>
                                    </div>
                                </div>
                                <div style={{ fontWeight: 700 }}>🔥 {friend.streak || 0}</div>
                            </div>
                        ))}
                    </div>
                )
            ) : feed.length === 0 ? (
                <div style={{ padding: '24px', borderRadius: '16px', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.72)' }}>
                    No friend activity yet. Add some friends to see updates here.
                </div>
            ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                    {feed.map((item) => (
                        <div key={item.id} style={{ padding: '16px', borderRadius: '16px', background: 'rgba(255,255,255,0.08)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center' }}>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <div style={{ fontSize: '2rem' }}>{item.friend_avatar || '👤'}</div>
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{item.friend_name || 'Friend'}</div>
                                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                            {item.title}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ fontSize: '1.6rem' }}>{item.icon_emoji || '✨'}</div>
                            </div>
                            <p style={{ margin: '12px 0 8px', color: 'rgba(255,255,255,0.85)' }}>{item.description}</p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.68)', fontSize: '0.9rem' }}>
                                <span>{item.amount > 0 ? `+${item.amount} DadBucks` : 'Shared update'}</span>
                                <span>{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
